/**
 * IntakeAssignmentView - Patient-Centric Workflow
 *
 * Shows unassigned patients and recommends providers for assignment
 */

import { useState, useMemo, useEffect } from 'react';
import { Box, Typography, Button, Alert, ButtonGroup } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import { CapacitySnapshot } from '../types/capacity.types';
import { IntakePatient, AutoAssignmentResult, ProviderRecommendation, AlternativeAssignmentOptions } from '../types/intake.types';
import IntakePatientTable from '../components/IntakePatientTable';
import IntakeAssignmentFilters from '../components/IntakeAssignmentFilters';
import AutoAssignmentReviewDialog from '../components/AutoAssignmentReviewDialog';
import { getMockIntakePatients, addTerritoryToProviders, getMockPanelSizes } from '../mocks/mockIntakePatients';
import {
  getProviderRecommendations,
  filterViableRecommendations,
  findAlternativeDates,
  findNextBestProviders,
  generateProvisionalOption,
} from '../engine/providerRecommendationEngine';
import { autoAssignPatients, createIntakeVisit, assignPatientToProvider } from '../engine/intakeAssignmentEngine';
import { overlaySimulation } from '../simulation/simulationEngine';
import { useIntakeQueue } from '~/providers/IntakeQueueProvider';

interface IntakeAssignmentViewProps {
  snapshot: CapacitySnapshot;
}

export default function IntakeAssignmentView({ snapshot }: IntakeAssignmentViewProps) {
  // Mock patient data
  const [patients, setPatients] = useState<IntakePatient[]>(() => getMockIntakePatients());

  // Merge incoming referral-sourced patients from intake queue
  const { queuedPatients, markAssigned } = useIntakeQueue();
  useEffect(() => {
    if (queuedPatients.length === 0) return;
    setPatients((prev) => {
      const existingIds = new Set(prev.map((p) => p.id));
      const newOnes = queuedPatients.filter((p) => !existingIds.has(p.id));
      return newOnes.length > 0 ? [...newOnes, ...prev] : prev;
    });
  }, [queuedPatients]);

  // Territory and panel data
  const territoryMap = useMemo(() => addTerritoryToProviders(), []);
  const panelSizes = useMemo(() => getMockPanelSizes(), []);

  // Expanded patients for viewing recommendations (support multiple)
  const [expandedPatientIds, setExpandedPatientIds] = useState<Set<number>>(new Set());

  // Filters
  const [targetUtilization, setTargetUtilization] = useState(75);
  const [selectedTerritories, setSelectedTerritories] = useState<string[]>(['North', 'South', 'East', 'West']);
  const [selectedPriorities, setSelectedPriorities] = useState<('HIGH' | 'MEDIUM' | 'LOW')[]>(['HIGH', 'MEDIUM', 'LOW']);
  const [selectedProviderType, setSelectedProviderType] = useState<'RN' | 'PT' | 'OT'>('RN');

  // Auto-assignment state
  const [autoAssignResult, setAutoAssignResult] = useState<AutoAssignmentResult | null>(null);
  const [showAutoAssignDialog, setShowAutoAssignDialog] = useState(false);

  // Simulated capacity snapshot (with assignments overlayed)
  const [simulatedSnapshot, setSimulatedSnapshot] = useState<CapacitySnapshot>(snapshot);

  // Get available territories (from both providers and patients)
  const availableTerritories = useMemo(() => {
    const providerTerritories = Object.values(territoryMap);
    const patientTerritories = patients.map((p) => p.territory);
    return Array.from(new Set([...providerTerritories, ...patientTerritories])).sort();
  }, [territoryMap, patients]);

  // Filter patients based on current filters
  const filteredPatients = useMemo(() => {
    return patients.filter((patient) => {
      if (patient.status === 'assigned') return true; // Show assigned patients
      if (!selectedTerritories.includes(patient.territory)) return false;
      if (!selectedPriorities.includes(patient.priority)) return false;
      return true;
    });
  }, [patients, selectedTerritories, selectedPriorities]);

  // Get unassigned patients only
  const unassignedPatients = useMemo(() => {
    return filteredPatients.filter((p) => p.status === 'unassigned');
  }, [filteredPatients]);

  // Build provider metadata for recommendation engine
  const providerMetadata = useMemo(() => {
    const uniqueProviders = new Map<number, any>();

    simulatedSnapshot.providers.forEach((day) => {
      if (!uniqueProviders.has(day.providerId)) {
        uniqueProviders.set(day.providerId, {
          providerId: day.providerId,
          providerName: day.providerName,
          role: day.role,
          territory: territoryMap[day.providerId] || 'Unknown',
          panelSize: panelSizes[day.providerId] || 0,
        });
      }
    });

    return Array.from(uniqueProviders.values());
  }, [simulatedSnapshot.providers, territoryMap, panelSizes]);

  // Filter provider metadata by selected provider type
  const filteredProviderMetadata = useMemo(() => {
    return providerMetadata.filter((provider) => provider.role === selectedProviderType);
  }, [providerMetadata, selectedProviderType]);

  // Generate recommendations for all unassigned patients
  const recommendationsMap = useMemo(() => {
    const map: { [patientId: number]: ProviderRecommendation[] } = {};

    unassignedPatients.forEach((patient) => {
      // Override patient's required role with the selected provider type
      const patientWithSelectedRole = {
        ...patient,
        requiredRole: selectedProviderType,
      };

      map[patient.id] = getProviderRecommendations(
        patientWithSelectedRole,
        simulatedSnapshot.providers,
        filteredProviderMetadata,
        targetUtilization
      );
    });

    return map;
  }, [unassignedPatients, simulatedSnapshot, filteredProviderMetadata, targetUtilization, selectedProviderType]);

  // Generate alternatives for patients with no viable recommendations
  const alternativesMap = useMemo(() => {
    const map: { [patientId: number]: AlternativeAssignmentOptions } = {};

    unassignedPatients.forEach((patient) => {
      const recommendations = recommendationsMap[patient.id] || [];
      const viableRecommendations = filterViableRecommendations(recommendations, false);

      // Only generate alternatives if no viable recommendations
      if (viableRecommendations.length === 0) {
        // Override patient's required role with the selected provider type
        const patientWithSelectedRole = {
          ...patient,
          requiredRole: selectedProviderType,
        };

        const alternativeDates = findAlternativeDates(
          patientWithSelectedRole,
          simulatedSnapshot.providers,
          filteredProviderMetadata,
          targetUtilization,
          3
        );

        const nextBestProviders = findNextBestProviders(
          patientWithSelectedRole,
          simulatedSnapshot.providers,
          filteredProviderMetadata,
          targetUtilization
        );

        const provisionalOption = generateProvisionalOption(patientWithSelectedRole);

        map[patient.id] = {
          alternativeDates,
          nextBestProviders,
          provisionalOption,
        };
      }
    });

    return map;
  }, [unassignedPatients, simulatedSnapshot, filteredProviderMetadata, targetUtilization, recommendationsMap, selectedProviderType]);

  // Handle patient selection (toggle expand/collapse)
  const handleSelectPatient = (patientId: number) => {
    setExpandedPatientIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(patientId)) {
        newSet.delete(patientId);
      } else {
        newSet.add(patientId);
      }
      return newSet;
    });
  };

  // Handle expand all
  const handleExpandAll = () => {
    const allUnassignedIds = unassignedPatients.map((p) => p.id);
    setExpandedPatientIds(new Set(allUnassignedIds));
  };

  // Handle collapse all
  const handleCollapseAll = () => {
    setExpandedPatientIds(new Set());
  };

  // Handle manual assignment
  const handleAssign = (patientId: number, providerId: number, providerName: string) => {
    const patient = patients.find((p) => p.id === patientId);
    if (!patient) return;

    // Create assignment
    const assignment = assignPatientToProvider(patient, providerId, providerName);

    // Create simulated intake visit
    const intakeVisit = createIntakeVisit(patient, providerId);

    // Overlay simulation on capacity snapshot
    const updatedProviders = overlaySimulation(
      simulatedSnapshot.providers,
      [intakeVisit],
      providerId
    );

    // Update simulated snapshot
    setSimulatedSnapshot({
      ...simulatedSnapshot,
      providers: updatedProviders,
    });

    // Update patient status
    setPatients((prev) =>
      prev.map((p) =>
        p.id === patientId
          ? { ...p, status: 'assigned', assignedProviderId: providerId, assignedProviderName: providerName }
          : p
      )
    );

    // Clear expansion for this patient
    setExpandedPatientIds((prev) => {
      const newSet = new Set(prev);
      newSet.delete(patientId);
      return newSet;
    });

    markAssigned(patientId, providerName, patient.intakeVisitDate);
  };

  // Handle auto-assign
  const handleAutoAssign = () => {
    // Override patients' required role with the selected provider type
    const patientsWithSelectedRole = unassignedPatients.map((patient) => ({
      ...patient,
      requiredRole: selectedProviderType,
    }));

    const result = autoAssignPatients(
      patientsWithSelectedRole,
      simulatedSnapshot.providers,
      filteredProviderMetadata,
      targetUtilization
    );

    setAutoAssignResult(result);
    setShowAutoAssignDialog(true);
  };

  // Confirm auto-assignment
  const handleConfirmAutoAssign = () => {
    if (!autoAssignResult) return;

    // Apply all assignments
    let updatedProviders = simulatedSnapshot.providers;

    autoAssignResult.assignments.forEach((assignment) => {
      if (assignment.status === 'success' || assignment.status === 'conflict') {
        const patient = patients.find((p) => p.id === assignment.patientId);
        if (patient) {
          // Create intake visit
          const intakeVisit = createIntakeVisit(patient, assignment.providerId);

          // Overlay on capacity
          updatedProviders = overlaySimulation(updatedProviders, [intakeVisit], assignment.providerId);

          // Update patient
          setPatients((prev) =>
            prev.map((p) =>
              p.id === patient.id
                ? {
                    ...p,
                    status: 'assigned',
                    assignedProviderId: assignment.providerId,
                    assignedProviderName: assignment.providerName,
                  }
                : p
            )
          );

          markAssigned(assignment.patientId, assignment.providerName, patient.intakeVisitDate);
        }
      }
    });

    // Update snapshot
    setSimulatedSnapshot({
      ...simulatedSnapshot,
      providers: updatedProviders,
    });

    // Close dialog
    setShowAutoAssignDialog(false);
    setAutoAssignResult(null);
  };

  // Handle alternative date assignment
  const handleAssignAlternativeForTable = (patientId: number, date: Date, providerName?: string) => {
    const patient = patients.find((p) => p.id === patientId);
    if (!patient || !providerName) return;

    // Find provider ID by name (in real implementation, would be passed from alternatives)
    const providerMeta = providerMetadata.find((p) => p.providerName === providerName);
    if (!providerMeta) return;

    handleAssignAlternativeInternal(patientId, providerMeta.providerId, providerName, date);
  };

  // Handle alternative date assignment (from dialog)
  const handleAssignAlternative = (patientId: number, providerId: number, providerName: string, newDate: Date) => {
    handleAssignAlternativeInternal(patientId, providerId, providerName, newDate);
  };

  // Internal helper for alternative assignment
  const handleAssignAlternativeInternal = (patientId: number, providerId: number, providerName: string, date: Date) => {
    const patient = patients.find((p) => p.id === patientId);
    if (!patient) return;

    // Update patient's intake date to the alternative date
    const updatedPatient = { ...patient, intakeVisitDate: date };

    // Create simulated intake visit with new date
    const intakeVisit = createIntakeVisit(updatedPatient, providerId);

    // Overlay simulation on capacity snapshot
    const updatedProviders = overlaySimulation(
      simulatedSnapshot.providers,
      [intakeVisit],
      providerId
    );

    // Update simulated snapshot
    setSimulatedSnapshot({
      ...simulatedSnapshot,
      providers: updatedProviders,
    });

    // Update patient status and date
    setPatients((prev) =>
      prev.map((p) =>
        p.id === patientId
          ? {
              ...p,
              status: 'assigned',
              assignedProviderId: providerId,
              assignedProviderName: providerName,
              intakeVisitDate: date,
            }
          : p
      )
    );

    // Clear expansion for this patient
    setExpandedPatientIds((prev) => {
      const newSet = new Set(prev);
      newSet.delete(patientId);
      return newSet;
    });

    markAssigned(patientId, providerName, date);

    // Close dialog if open
    setShowAutoAssignDialog(false);
    setAutoAssignResult(null);
  };

  // Handle next best provider assignment (with warnings)
  const handleAssignNextBest = (patientId: number, providerId: number, providerName: string) => {
    handleAssign(patientId, providerId, providerName);

    // Close dialog if open
    setShowAutoAssignDialog(false);
    setAutoAssignResult(null);
  };

  // Handle provisional marketplace assignment
  const handleCreateProvisional = (patientId: number, date: Date) => {
    const patient = patients.find((p) => p.id === patientId);
    if (!patient) return;

    // For now, mark as "assigned" to a provisional shift
    // In real implementation, this would create a marketplace shift
    setPatients((prev) =>
      prev.map((p) =>
        p.id === patientId
          ? {
              ...p,
              status: 'assigned',
              assignedProviderId: 0,
              assignedProviderName: 'Marketplace (Provisional)',
              intakeVisitDate: date,
            }
          : p
      )
    );

    // Clear expansion for this patient
    setExpandedPatientIds((prev) => {
      const newSet = new Set(prev);
      newSet.delete(patientId);
      return newSet;
    });

    markAssigned(patientId, 'Marketplace (Provisional)', date);

    // Close dialog if open
    setShowAutoAssignDialog(false);
    setAutoAssignResult(null);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h5">Intake Assignment</Typography>
          <Typography variant="body2" color="text.secondary">
            Assign unassigned patients to provider panels
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <ButtonGroup variant="outlined" size="small">
            <Button
              startIcon={<UnfoldMoreIcon />}
              onClick={handleExpandAll}
              disabled={unassignedPatients.length === 0}
            >
              Expand All
            </Button>
            <Button
              startIcon={<UnfoldLessIcon />}
              onClick={handleCollapseAll}
              disabled={expandedPatientIds.size === 0}
            >
              Collapse All
            </Button>
          </ButtonGroup>
          <Button
            variant="contained"
            startIcon={<AutoAwesomeIcon />}
            onClick={handleAutoAssign}
            disabled={unassignedPatients.length === 0}
          >
            Auto-Assign All ({unassignedPatients.length})
          </Button>
        </Box>
      </Box>

      {/* Filters */}
      <IntakeAssignmentFilters
        targetUtilization={targetUtilization}
        onTargetUtilizationChange={setTargetUtilization}
        selectedTerritories={selectedTerritories}
        availableTerritories={availableTerritories}
        onTerritoriesChange={setSelectedTerritories}
        selectedPriorities={selectedPriorities}
        onPrioritiesChange={setSelectedPriorities}
        selectedProviderType={selectedProviderType}
        onProviderTypeChange={setSelectedProviderType}
      />

      {/* Patient Table with Inline Recommendations */}
      <IntakePatientTable
        patients={filteredPatients}
        expandedPatientIds={expandedPatientIds}
        onSelectPatient={handleSelectPatient}
        recommendations={recommendationsMap}
        alternatives={alternativesMap}
        targetUtilization={targetUtilization}
        onAssign={handleAssign}
        onAssignAlternative={handleAssignAlternativeForTable}
        onAssignNextBest={handleAssignNextBest}
        onCreateProvisional={handleCreateProvisional}
      />

      {/* Auto-Assignment Review Dialog */}
      <AutoAssignmentReviewDialog
        open={showAutoAssignDialog}
        result={autoAssignResult}
        onConfirm={handleConfirmAutoAssign}
        onCancel={() => {
          setShowAutoAssignDialog(false);
          setAutoAssignResult(null);
        }}
        onAssignAlternative={handleAssignAlternative}
        onAssignNextBest={handleAssignNextBest}
        onCreateProvisional={handleCreateProvisional}
      />
    </Box>
  );
}
